import * as readline from 'readline'
import {
  loadConfig,
  saveConfig,
  isConfigured,
  CONFIG_FILE,
  getApiUrl,
} from '../lib/config'
import { fetchUserInfo } from '../lib/api'

function question(rl: readline.Interface, prompt: string): Promise<string> {
  return new Promise((resolve) => {
    rl.question(prompt, (answer) => {
      resolve(answer.trim())
    })
  })
}

export async function initCommand(): Promise<void> {
  const rl = readline.createInterface({
    input: process.stdin,
    output: process.stdout,
  })

  console.log('\n🛫 FlightDesk CLI Setup\n')

  try {
    // Check if already configured
    if (isConfigured()) {
      const config = loadConfig()
      console.log('FlightDesk is already configured.')
      console.log(`  API Key: ${config.apiKey?.substring(0, 20)}...`)
      console.log(`  Organizations: ${config.organizations.map(o => o.name).join(', ')}`)
      console.log('')

      const reconfigure = await question(rl, 'Reconfigure? (y/N): ')
      if (reconfigure.toLowerCase() !== 'y') {
        console.log('\nConfiguration unchanged.')
        console.log('Use "flightdesk org refresh" to update organizations.')
        console.log('Use "flightdesk org switch <name>" to change active organization.')
        return
      }
    }

    // Get API key
    console.log('Enter your FlightDesk API key.')
    console.log('You can find this at https://flightdesk.dev/settings/api-keys\n')

    const apiKey = await question(rl, 'API Key: ')
    if (!apiKey) {
      console.error('API Key is required')
      return
    }

    // Use the API URL from --api/--dev flags or default to production
    const apiUrl = getApiUrl()

    // Test the connection and fetch user's organizations
    console.log('\nConnecting to FlightDesk...')

    try {
      const userInfo = await fetchUserInfo(apiKey, apiUrl)

      const orgs = userInfo.organizations || []
      if (orgs.length === 0) {
        console.error('❌ No organizations found for this user')
        console.log('Please make sure you are a member of at least one organization.')
        return
      }

      const primaryEmail = userInfo.emails?.find(e => e.primary)?.email || userInfo.emails?.[0]?.email || 'unknown'
      console.log('✅ Connection successful!')
      console.log(`   Logged in as: ${primaryEmail}`)
      console.log(`   Organizations: ${orgs.length}`)

      // Map to our OrganizationInfo format
      const organizations = orgs.map(m => ({
        id: m.organization.id,
        name: m.organization.name,
      }))

      // Let user choose active org if multiple
      let activeOrganization: string
      if (organizations.length === 1) {
        activeOrganization = organizations[0].id
        console.log(`\n   Active organization: ${organizations[0].name}`)
      } else {
        console.log('\nYour organizations:')
        organizations.forEach((org, i) => {
          console.log(`  ${i + 1}. ${org.name}`)
        })
        const choice = await question(rl, `\nSelect active organization (1-${organizations.length}): `)
        const choiceIndex = Number.parseInt(choice, 10) - 1
        if (choiceIndex < 0 || choiceIndex >= organizations.length) {
          console.error('Invalid selection, using first organization')
          activeOrganization = organizations[0].id
        } else {
          activeOrganization = organizations[choiceIndex].id
        }
      }

      // Save configuration
      const config = loadConfig()
      config.apiKey = apiKey
      config.apiUrl = apiUrl
      config.organizations = organizations
      config.activeOrganization = activeOrganization
      saveConfig(config)

      console.log(`\n✅ Configuration saved to ${CONFIG_FILE}`)
      console.log('\nYou can now use:')
      console.log('  flightdesk project list        - List projects')
      console.log('  flightdesk task create         - Create a task')
      console.log('  flightdesk org list            - Show organizations')
      console.log('  flightdesk org switch <name>   - Switch active organization')
    } catch (error) {
      console.error(`\n❌ Connection failed: ${error}`)
      console.log('\nPlease check:')
      console.log('  - Your API key is correct')
      console.log('  - You have network connectivity')
      console.log('  - The FlightDesk API is reachable')
    }
  } finally {
    rl.close()
  }
}
